import cls from "classnames";
import { useState, useEffect, useRef } from "react";
import format from "date-fns/format";
import Glider from 'react-glider';
import 'glider-js/glider.min.css';

import { ChevronRightIcon, ArrowIcon } from "@qoohoo/common/library/Icons";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import { ButtonPrimary } from "@qoohoo/common/components/library/Button";

import { QOOHOO_WEBSITE_DOMAIN } from "constants/env";
import httpf from "utils/api/frontend/httpf";
import useSupercheckout from "hooks/useSupercheckout";

import useEntityStore from "store/entityStore";
import usePreferencesStore from "store/preferencesStore";
import useCustomerStore from "store/customerStore";

import GlobeIcon from "components/icons/GlobeIcon";
import ErrorFaceIcon from "components/icons/ErrorFaceIcon";

export default function SlotPicker({ handleBackButton }) {
  const entity = useEntityStore((state) => state.entity);
  const preferences = usePreferencesStore((state) => state.preferences);
  const customer = useCustomerStore((state) => state.customer);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [areSlotsLoading, setAreSlotsLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [timezone, setTimezone] = useState(entity.data.meta.timezone);

  const areAllSlotsBooked = availableTimeSlots.length == 0;

  useEffect(() => {
    const start_date = new Date();
    const end_date = new Date();
    end_date.setDate(end_date.getDate() + 90); // Show available dates in a 90 day frame

    const availableDates = getAvailableDates(
      entity.data.meta.availability,
      start_date,
      end_date
    );
    setAvailableDates(availableDates);

    fetchTimeSlots(availableDates[0]);
  }, []);

  function getAvailableDates(availability, startDate, endDate) {
    let _availableDates = [];

    const isSomeDayAvailable = Object.keys(availability).some(
      (day) => availability[day].available
    );

    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    if (isSomeDayAvailable) {
      let date = startDate;

      while (date <= endDate) {
        const day = days[date.getUTCDay()];
        const dateIsAvailable = availability[day].available;

        if (dateIsAvailable) {
          _availableDates.push(date);
        }

        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);

        date = nextDate;
      }
    }

    return _availableDates;
  }

  function formatDate(date) {
    return (
      new Date(date).getFullYear() +
      "-" +
      (new Date(date).getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      new Date(date).getDate()
    );
  }

  function isPastTime(time, viewingDate) {
    const presentDate = new Date();

    if (viewingDate > presentDate) return false;

    const presentTime = presentDate.toLocaleTimeString();
    const [presentHour, presentMinute, presentSecond] = presentTime.split(":");
    const [toCheckHour, toCheckMinute, toCheckSecond] = time.split(":");

    return (
      presentHour * 3600 +
        presentMinute * 60 +
        presentSecond -
        (toCheckHour * 3600 + toCheckMinute * 60 + toCheckSecond) >
      0
    );
  }

  function fetchTimeSlots(date) {
    if(date==selectedDate) return;

    setAreSlotsLoading(true);
    setSelectedDate(date);

    const formattedDate = formatDate(date);

    httpf
      .get(
        `${QOOHOO_WEBSITE_DOMAIN}/api/meeting/slots?id=${entity.data.uid}&booking_date=${formattedDate}`
      )
      .then((res) => {
        const availableSlots = res.data.available_slots;

        if (availableSlots) {
          const futureTimeSlots = availableSlots.filter(
            (time) => !isPastTime(time, date)
          );

          setAvailableTimeSlots(futureTimeSlots);
          setSelectedSlot(futureTimeSlots[0]);
        }
      })
      .catch((err) => { 
        console.log(err);

        setAvailableTimeSlots([]);
      })
      .finally(() => {
        setAreSlotsLoading(false);
      });
  }

  return (
    <div className="h-full md:h-auto w-full flex flex-col gap-6 relative">
      <div className="h-full md:h-auto w-full flex flex-col md:border md:border-[#E8E8E8] rounded-lg overflow-hidden">
        <Header handleBackButton={handleBackButton}/>
        <div className="h-full md:h-auto w-full flex flex-col p-5 sm:px-8 md:px-5 gap-6 overflow-auto scrollbar-hide">
          <DateSelector
            dates={availableDates}
            selectedDate={selectedDate}
            onClick={fetchTimeSlots}
          />
          <TimePicker
            isLoading={areSlotsLoading}
            onClick={setSelectedSlot}
            selectedSlot={selectedSlot}
            timeSlots={availableTimeSlots}
          />
          <div className="block md:hidden">
            <TimezoneSelector value={timezone} onChange={setTimezone} />
          </div>
        </div>
        <div className="fixed left-0 right-0 bottom-0 md:static bg-white shadow-[0px_-4px_15px_0px_#0000000D] px-5 pt-4 pb-6 sm:pb-4">
          <BookSlotButton disabled={areAllSlotsBooked} date={selectedDate} slot={selectedSlot} />
        </div>
      </div>
      <div className="hidden md:block">
        <TimezoneSelector value={timezone} onChange={setTimezone} />
      </div>
    </div>
  );
}

function BookSlotButton({ date, slot, disabled = false }) {
  const entity = useEntityStore((state) => state.entity);
  const openCheckout = useSupercheckout();

  function formatDate(date) {
    return (
      new Date(date).getFullYear() +
      "-" +
      (new Date(date).getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      new Date(date).getDate()
    );
  }

  function openMiniCheckout() {
    openCheckout({
      entity_id: entity.data.uid,
      entity_type: entity.data.type,
      booking_date: formatDate(date),
      start_at: slot,
    });
  }

  const formattedDate = `${format(date, "d MMM")}'${format(date, "yy")}`;

  return (
    <ButtonPrimary
      onClick={openMiniCheckout}
      disabled={disabled}
      className="bg-primary w-full select-none max-w-md mx-auto"
      doOpacityForDisabled
    >
      <div className="flex flex-col items-center text-white">
        <span className="font-bold text-base">Book a slot</span>
        <span className="text-[11px] leading-4 font-normal">{`for ${formattedDate}`}</span>
      </div>
    </ButtonPrimary>
  );
}

function TimezoneSelector({ className, value, onChange }) {
  return (
    <div className={cls("text-[#6A7175] flex items-center gap-1", className)}>
      <GlobeIcon className="stroke-current h-4 w-4" />
      {`Time zone: ${value}`}
    </div>
  );
}

function Header({ handleBackButton }) {
  
  const onBackClick = () => {
    handleBackButton && handleBackButton();
  }

  return (
    <div className="w-full py-4 px-2 text-center flex items-center justify-between md:justify-center bg-white shadow-[0px_4px_10px_0px_#0000000D]">
      <div className="rotate-180 md:hidden cursor-pointer p-1" onClick={onBackClick}>
        <ArrowIcon className="h-5 w-5" color={'#6A7175'}/>
      </div>
      <span className="text-xl font-bold text-center text-[#16191B]">
        Select your available slot
      </span>
      {/* hidden element to get left-center-empty layout */}
      <div className="invisible md:hidden p-1" onClick={onBackClick}>
        <ArrowIcon className="h-5 w-5" color={'#6A7175'}/>
      </div>
    </div>
  );
}

function DateSelector({ dates, selectedDate, onClick }) {
  const [gliderRef, setGliderRef] = useState(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-base">Select a date</span>
        <div className="flex items-center gap-6 text-[#6A7175]">
          <div className="p-1 -my-0.5 cursor-pointer" ref={leftArrowRef}>
            <ChevronRightIcon className="rotate-180 h-5 w-5 stroke-current" />
          </div>
          <div className="p-1 -my-0.5 cursor-pointer" ref={rightArrowRef}>
            <ChevronRightIcon className="h-5 w-5 stroke-current" />
          </div>
        </div>
      </div>
      <div className="w-full overflow-hidden relative">
      <Glider
        className="glider-container w-[calc(100%+0.75rem)]"
        scrollLock
        resizeLock
        hasArrows
        draggable
        scrollLockDelay={100}
        slidesToShow={3}
        slidesToScroll={1}
        dragVelocity={1}
        rewind={false}
        arrows={{
          prev: leftArrowRef.current,
          next: rightArrowRef.current,
        }}
        responsive={[
          { breakpoint: 320,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 3,
            }
          },
          {
            breakpoint: 440,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 4,
            }
          },
          { breakpoint: 550,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 5,
            }
          },
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 3,
            }
          },
          {
            breakpoint: 1200,
            settings: {
              slidesToShow:5,
              slidesToScroll: 4,
            }
          }
        ]}
        ref={setGliderRef}
      >
        {dates.map((date) => {
          const isSelected = selectedDate == date;

          const selectedWeekDay = format(date, "EEE");

          const formattedDate = format(date, "d MMM");

          return (
            <div className="!min-w-0 mr-3" key={date}>
              <div
                onClick={() => onClick(date)}
                className={cls(
                  "py-4 mr-0.5 whitespace-nowrap text-center flex flex-col gap-0.5 border rounded-[0.25rem] overflow-hidden select-none cursor-pointer shrink-0",
                  isSelected
                    ? "bg-primary-light border-primary"
                    : "bg-transparent border-[#E8E8E8]"
                )}
              >
                <span className="text-[#6A7175] text-xs">
                  {selectedWeekDay}
                </span>
                <span
                  className={cls(
                    "font-semibold text-base whitespace-nowrap",
                    isSelected ? "text-primary" : "text-[#16191B]"
                  )}
                >
                  {formattedDate}
                </span>
              </div>
            </div>
          );
        })}
      </Glider>
      </div>
    </div>
  );
}

function TimePicker({ isLoading, timeSlots, selectedSlot, onClick }) {
  function formatTime(timeStr) {
    let hours = +timeStr.split(":")[0];
    let minutes = +timeStr.split(":")[1];

    let ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    minutes = minutes.toString().padStart(2, "0");
    let strTime = hours + ":" + minutes + " " + ampm;
    return strTime;
  }

  const areAllSlotsBooked = timeSlots.length == 0;

  function renderContent() {
    let content;

    if (isLoading) {
      content = (
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
          <Spinner className="h-8 w-8" color="#16191B" />
        </div>
      );
    } else if (areAllSlotsBooked) {
      content = (
        <div className="h-full w-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <ErrorFaceIcon />
            <span className="text-[#6A7175] text-base select-none">
              All slots are booked for the day!
            </span>
          </div>
        </div>
      );
    } else {
      content = (
        <div className="w-full grid grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] auto-rows-min gap-2">
          {timeSlots.map((slot) => {
            const isSelected = slot == selectedSlot;

            return (
              <div
                onClick={() => onClick(slot)}
                key={slot}
                className={cls(
                  "h-fit py-3 text-center select-none cursor-pointer rounded-[0.25rem] border whitespace-nowrap text-sm font-semibold",
                  isSelected
                    ? " border-primary bg-primary-light text-primary"
                    : "border-[#E8E8E8] bg-transparent text-[#16191B]"
                )}
              >
                {formatTime(slot)}
              </div>
            );
          })}
        </div>
      );
    }

    return content;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-base font-semibold select-none">Pick a time</div>
      <div className="w-full h-[210px] pb-2 relative overflow-auto scrollbar-hide">
        {renderContent()}
      </div>
    </div>
  );
}
